import { useState, useEffect } from 'react';
import { message } from 'antd';
import {
  adminService,
  Payment,
  PaymentStats,
  PaymentStatus,
  PaymentType,
  PaymentMethod,
} from '../../api/services/adminService';

export const usePayments = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0,
  });
  const [filters, setFilters] = useState<{
    status?: PaymentStatus;
    type?: PaymentType;
    method?: PaymentMethod;
  }>({});

  const fetchPayments = async (page = 1, limit = 10) => {
    setLoading(true);
    try {
      const response = await adminService.getPayments(page, limit, filters);
      const apiData = response as any;
      setPayments(apiData.data || apiData.payments || []);
      setPagination(
        apiData.pagination || { total: 0, page: 1, limit: 10, pages: 0 }
      );
    } catch {
      message.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const result = await adminService.getPaymentStats();
      setStats(result);
    } catch {
      // Stats are non-critical; fail silently
    } finally {
      setStatsLoading(false);
    }
  };

  const handleComplete = async (id: string) => {
    try {
      await adminService.completePayment(id);
      message.success('Payment marked as completed');
      fetchPayments(pagination.page, pagination.limit);
    } catch {
      message.error('Failed to complete payment');
    }
  };

  const handleFail = async (id: string) => {
    try {
      await adminService.failPayment(id);
      message.success('Payment marked as failed');
      fetchPayments(pagination.page, pagination.limit);
    } catch {
      message.error('Failed to update payment');
    }
  };

  const handleRefund = async (id: string) => {
    try {
      await adminService.refundPayment(id);
      message.success('Payment refunded');
      fetchPayments(pagination.page, pagination.limit);
    } catch {
      message.error('Failed to refund payment');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminService.deletePayment(id);
      message.success('Payment deleted');
      fetchPayments(pagination.page, pagination.limit);
    } catch {
      message.error('Failed to delete payment');
    }
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  const handleTableChange = (pag: any) => {
    fetchPayments(pag.current, pag.pageSize);
  };

  useEffect(() => {
    fetchPayments(1, pagination.limit);
  }, [filters]);

  useEffect(() => {
    fetchStats();
  }, []);

  return {
    payments,
    stats,
    loading,
    statsLoading,
    pagination,
    filters,
    handleComplete,
    handleFail,
    handleRefund,
    handleDelete,
    handleFilterChange,
    handleTableChange,
    refetch: () => fetchPayments(pagination.page, pagination.limit),
  };
};
